import React from "react";
import * as c from "./lcAnchorHeading.module.scss";
import { AnchorLinkIcon } from "@components/icons/anchorLinkIcon";
import classNames from "classnames";
import { useCopyToClipboard } from "@hooks/useCopyToClipboard";
import { slugify } from "@utils/helpers";

// Given a @contentful/rich-text-type root node, returns a string
// representing a valid HTML id attribute comrpised of text subnodes
const constructHtmlIdFromNode = (rootNode) =>
  rootNode.content
    .filter((c) => c.nodeType === "text")
    .map((f) => f.value)
    .join("")
    .replace(/[^a-zA-Z0-9\-\s]/g, "") // remove any char NOT in [a-z, A-Z, 0-9, -, \r\n\t\f\v]
    .trim()
    .replace(/\s+/g, "-") // replace any sequence of 1 or more whitespaces with a single dash
    .toLowerCase();

// Convert text to slug for anchor ID
const constructHtmlIdFromText = (text) => slugify(text);

const LcAnchorHeading = ({ level, node, children, anchorId, className, currentPage, noAnchorLinks = false }) => {
  // Support both Contentful node structure and simple text/anchorId
  const nodeId = anchorId || (node ? constructHtmlIdFromNode(node) : constructHtmlIdFromText(children));
  const HeadingTag = `h${level}`;
  const [copied, copyToClipboard] = useCopyToClipboard();

  const handleCopy = () => {
    let url = `${window.location.origin}${window.location.pathname}`;

    // Add page parameter if currentPage is provided and not page 1
    if (currentPage && currentPage > 1) {
      url += `?page=${currentPage}`;
    }

    url += `#${nodeId}`;
    copyToClipboard(url);
  };

  return (
    <HeadingTag id={nodeId} className={classNames(c.anchorHeading, { [c.noAnchors]: noAnchorLinks }, className)}>
      {!noAnchorLinks && (
        <>
          {/* Desktop anchor - positioned to the left of heading */}
          <div className={c.anchorContainer}>
            <button
              type="button"
              className={classNames(c.anchorLink, { [c.copied]: copied })}
              onClick={handleCopy}
              aria-label={`Copy link to "${children}"`}
            >
              <AnchorLinkIcon />
            </button>

            <div className={classNames(c.copiedAnchor, { [c.visible]: copied })} aria-live="polite" data-nosnippet>
              Link copied
            </div>
          </div>
        </>
      )}
      <span className={c.headingContent}>
        {children}
        {!noAnchorLinks && (
          <>
            {/* Mobile anchor - positioned inline at end of text */}
            <div className={c.anchorContainer}>
              <button
                type="button"
                className={classNames(c.anchorLink, { [c.copied]: copied })}
                onClick={handleCopy}
                aria-label={`Copy link to "${children}"`}
              >
                <AnchorLinkIcon />
              </button>
              <div className={classNames(c.copiedAnchor, { [c.visible]: copied })} aria-live="polite" data-nosnippet>
                Link copied
              </div>
            </div>
          </>
        )}
      </span>
    </HeadingTag>
  );
};

export default LcAnchorHeading;
